'use client'

import { useAuthContext } from '@/hooks/use-auth-context'
import { orgAdminAllowed } from '@/lib/org-access'
import { useOptionalActiveOrg } from '@/providers/active-scope-provider'

/**
 * Whether the signed-in user is an admin of the active org.
 *
 * Drives every client-side org-admin surface: the org-workspace rail menu,
 * the switcher's workspace entry, the credit widgets' billing link, and the
 * billing write CTAs (`useBillingAdminGate` delegates here). The server gates
 * all of them on `require_org_admin` and stays authoritative — this only
 * decides what is rendered, so a stale value can never turn into access.
 *
 * Reads the role from the server-resolved active scope FIRST, falling back to
 * the client auth context. That order is load-bearing, not a preference: the
 * auth context is populated in a `useEffect` after a dynamic import and a
 * `/api/session/refresh` round trip, so it is empty during SSR and for the
 * first client frames. Deriving navigation from it alone made an admin render
 * the brand rail on every server pass and watch it swap after bootstrap. The
 * active scope comes from the root layout's `resolveActiveScope()` and is
 * therefore correct in the first byte of HTML.
 *
 * The two sources read the same `/api/auth/me` org claim, so they agree; the
 * auth context is consulted second only because it is the fresher of the two
 * after an in-page org switch.
 *
 * The RSC counterpart is `orgAdminAllowed(ctx?.org?.role)` off
 * `getServerOrgContext()`; all three call the same predicate.
 */
export function useOrgAdminGate(): boolean {
  const scopeOrg = useOptionalActiveOrg()
  const { org: authOrg } = useAuthContext()
  const role = authOrg?.role ?? scopeOrg?.role ?? null
  return orgAdminAllowed(role)
}
